import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, reduxForm, reset } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import FormControl from 'react-bootstrap/lib/FormControl';
import { GoogleReCaptchaProvider } from '@google-recaptcha/react';

import Loader from '../Loader';
import messages from '../../locale/jsonMessages';
import validate from './validate';
import fetch from '../../core/fetch'
import showToaster from '../../helpers/toasterMessages/showToaster';
import { sendContactEmail as query } from '../../lib/graphql';

import mailLogo from './mailblack.svg';
import caller from './callLogo.svg';
import addressLogo from './address.svg';
import cs from '../../components/commonStyle.css';
import s from './ContactForm.css';

const renderFormControl = ({ input, labelText, type, meta: { touched, error }, className, isDisabled, isRequiredInput }) => {
    const { formatMessage } = useIntl();
    return (
        <div>
            <label className='commonLabelText'>
                <span>{labelText}{isRequiredInput && <sup className='isRequiredStar'>*</sup>}</span>
            </label>
            <FormControl {...input} placeholder={labelText} type={type} className={className} disabled={isDisabled} />
            {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
        </div>
    );
};

const renderFormControlTextArea = ({ input, labelText, meta: { touched, error }, className, isRequiredInput }) => {
    const { formatMessage } = useIntl();
    return (
        <div>
            <label className='commonLabelText'>
                <span>{labelText}{isRequiredInput && <sup className='isRequiredStar'>*</sup>}</span>
            </label>
            <FormControl {...input} className={className} placeholder={labelText} componentClass="textarea" />
            {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
        </div>
    );
};

const renderCaptcha = ({ input, meta: { touched, error }, reCaptcha }) => {
    const { formatMessage } = useIntl();
    return (
        <div>
            {reCaptcha?.reCaptchaStatus === 'v2' ? (
                <ReCAPTCHA sitekey={reCaptcha?.v2Sitekey} onChange={input.onChange} />
            ) : (
                <GoogleReCaptchaProvider siteKey={reCaptcha?.v3Sitekey} type="v3" />
            )}
            {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
        </div>
    );
};

const ContactForm = (props) => {
    const { handleSubmit, submitting, error, reCaptcha } = props;

    const { formatMessage } = useIntl();
    const dispatch = useDispatch();

    const [contactLoading, setContactLoading] = useState(false);

    const email = useSelector(state => state?.siteSettings?.data?.email);
    const phoneNumber = useSelector(state => state?.siteSettings?.data?.phoneNumber);
    const address = useSelector(state => state?.siteSettings?.data?.address);

    const getErrorMessageByStatus = useCallback(async (status , isDemoWarning) => {
        if (status == 409) {
            return await showToaster({ messageId: 'duplicateRequestError', toasterType: 'error' });
        }

        if (status == 429) {
            return await showToaster({ messageId: 'tooManyRequestsError', toasterType: 'error' });
        }

        if(status == 400 && isDemoWarning){
            return await showToaster({ messageId: 'demoEmailWarning', toasterType: 'alert' });
        }

        return await showToaster({ messageId: 'invalidRecaptchaError', toasterType: 'error' });
    }, []);

    const getRecaptchaToken = useCallback(async (values) => {
        const captchaMode = reCaptcha?.reCaptchaStatus;

        if (captchaMode === 'v3') {
            if (!window?.grecaptcha || !reCaptcha?.v3Sitekey) {
                return null;
            }

            return window?.grecaptcha?.execute(reCaptcha?.v3Sitekey, { action: 'ContactForm' });
        }

        if (captchaMode === 'v2') {
            return values?.reCaptcha;
        }

        return null;
    }, [reCaptcha]);

    const handleClick = useCallback(async (values) => {
        const recaptchaToken = await getRecaptchaToken(values);
        if (!recaptchaToken) {
            showToaster({ messageId: 'commonError', toasterType: 'error' });
            return;
        }
        const variables = {
            phoneNumber: values.phoneNumber,
            name: values.name,
            email: values.email,
            contactMessage: values.contactMessage,
            recaptchaToken
        };

        setContactLoading(true);
        const resp = await fetch('/graphql', {
            method: 'post',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                query: query,
                variables
            }),
            credentials: 'include'
        });

        const { data } = await resp.json();

        setContactLoading(false);

        const result = data?.sendContactEmail?.status;

        const isDemoWarning = data?.sendContactEmail?.errorMessage == 'demoWarning'
        if (result == 200) {
            showToaster({ messageId: 'emailSent', toasterType: 'success' });
            dispatch(reset('ContactForm'));
            reCaptcha?.reCaptchaStatus === 'v2' && grecaptcha?.reset?.();
        } else {
            getErrorMessageByStatus(result, isDemoWarning)
            reCaptcha?.reCaptchaStatus === 'v2' && grecaptcha?.reset?.();
        }

    }, [reCaptcha, email, dispatch, getRecaptchaToken, getErrorMessageByStatus]);

    return (
        <div className={s.contactUsWrapper}>
            <div>
                {(email || phoneNumber || address) && (
                    <div className={s.space6}>
                        <h1 className={s.contactTitle}>
                            <FormattedMessage {...messages.contactFormInformation} />
                        </h1>
                    </div>
                )}
                <Row className={s.dFlex}>
                    {email && (
                        <Col lg={4} md={4} sm={4} xs={12} className={s.alignCenter}>
                            <div className={s.space6}>
                                <div className={s.iconMargin}><img src={mailLogo} className={s.mailIcon} /></div>
                                <h1 className={cx(s.contactTitle, s.subTitleText)}><FormattedMessage {...messages.contactFormEmail} /></h1>
                                <a href={`mailto:${email}`} className={s.linkText} target="_blank" >{email}</a>
                            </div>
                        </Col>
                    )}
                    {phoneNumber && (
                        <Col lg={4} md={4} sm={4} xs={12} className={s.alignCenter}>
                            <div className={s.space6}>
                                <div className={s.iconMargin}><img src={caller} className={s.mailIcon} /></div>
                                <h1 className={cx(s.contactTitle, s.subTitleText)}><FormattedMessage {...messages.contactFormCall} /></h1>
                                <a href={`tel:${phoneNumber}`} className={s.linkText} target="_blank" >{phoneNumber}</a>
                            </div>
                        </Col>
                    )}
                    {address && (
                        <Col lg={4} md={4} sm={4} xs={12} className={s.alignCenter}>
                            <div className={s.space6}>
                                <div className={s.iconMargin}><img src={addressLogo} className={s.mailIcon} /></div>
                                <h1 className={cx(s.contactTitle, s.subTitleText)}><FormattedMessage {...messages.contactFormAddress} /></h1>
                                <h4 className={s.addressText}>{address}</h4>
                            </div>
                        </Col>
                    )}
                </Row>
            </div>

            <div className={cx(s.marginTop)}>
                <div className={cx(s.formBackground,)}>
                    <div className={s.formContainerHeader}>
                        <h2 className={s.captionText}><FormattedMessage {...messages.contactForm} /></h2>
                    </div>
                    <div className={s.formContainer}>
                        {error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
                        <form onSubmit={handleSubmit(handleClick)}>
                            <div className="oneColumnGrid">
                                <div className='twoColumnGrid'>
                                    <Field name="name"
                                        type="text"
                                        component={renderFormControl}
                                        labelText={formatMessage(messages.nameInContact)}
                                        isRequiredInput
                                        className={cx('commonInputField', s.backgroundTwo, 'commonRightSideIconInput', 'contactInputRTL')} />

                                    <Field
                                        name="phoneNumber"
                                        type="text"
                                        component={renderFormControl}
                                        labelText={formatMessage(messages.phoneNumber)}
                                        isRequiredInput
                                        className={cx('commonInputField', s.backgroundThree, 'commonRightSideIconInput', 'contactInputRTL')} />
                                </div>

                                <Field
                                    name="email"
                                    type="text"
                                    component={renderFormControl}
                                    labelText={formatMessage(messages.email)}
                                    isRequiredInput
                                    className={cx('commonInputField', s.backgroundOne, 'commonRightSideIconInput', 'contactInputRTL')} />


                                <Field
                                    name="contactMessage"
                                    component={renderFormControlTextArea}
                                    labelText={formatMessage(messages.contactMessage)}
                                    isRequiredInput
                                    className={cx('commonTextAreaField', s.backgroundFour, 'contactTextInputRTL')} />


                                <div className={s.overFlowHidden}>
                                    <Field name="reCaptcha"
                                        component={renderCaptcha}
                                        reCaptcha={reCaptcha} />
                                </div>

                                <div className='singleBtnFlexEnd'>
                                    <Loader
                                        type="button"
                                        buttonType="submit"
                                        className={cx(cs.btnPrimary, 'loaderButtonFlex')}
                                        disabled={submitting}
                                        show={contactLoading}
                                        label={formatMessage(messages.sendmail)}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

ContactForm.propTypes = {
    reCaptcha: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    error: PropTypes.string,
};

export default reduxForm({
    form: 'ContactForm',
    validate
})(withStyles(cs, s)(ContactForm));
